import { useQuery } from "@tanstack/react-query";
import { SlotGameLaunchResponse } from "../types/slot";
import apiService from "./apiService";

export interface SlotGameItem {
  id: string;
  game_id: string;
  image: string;
  key: string;
  code: string;
  provider: string;
  providerName: string;
  providerNameKo: string;
  names: { ko: string; en: string };
  category: string;
  rtp: number | null;
  volatility: string | null;
  isNew: boolean;
  isFeatured: boolean;
  isPopular: boolean;
}

export interface ProviderFilter {
  code: string;
  name: string;
  nameKo: string;
  logo: string;
  gameCount: number;
}

export interface CategoryFilter {
  name: string;
  count: number;
}

export interface AllSlotGamesResponse {
  status: number;
  games: SlotGameItem[];
  providers: ProviderFilter[];
  categories: CategoryFilter[];
  pagination: {
    page: number;
    perPage: number;
    totalPages: number;
    totalCount: number;
  };
  message: { success: string; warning: string | null };
}

export function providerFilterKey(p: Pick<ProviderFilter, "code">): string {
  return p.code;
}

export function parseProviderFilterKey(key: string): { code: string } {
  return { code: key || "" };
}

export interface AllSlotGamesParams {
  provider?: string;
  category?: string;
  search?: string;
  featured?: boolean;
  popular?: boolean;
  isNew?: boolean;
  page?: number;
  perPage?: number;
  sort?: "popular" | "newest" | "name" | "provider" | "rtp";
}

export async function getAllSlotGames(
  params: AllSlotGamesParams = {}
): Promise<AllSlotGamesResponse> {
  const response = await apiService.postRaw<AllSlotGamesResponse>(
    "/api/Slot/getAllGames",
    {
      provider: params.provider || undefined,
      category: params.category || undefined,
      search: params.search || undefined,
      featured: params.featured || undefined,
      popular: params.popular || undefined,
      isNew: params.isNew || undefined,
      page: params.page || 1,
      perPage: params.perPage || 50,
      sort: params.sort || "popular",
    }
  );

  if (response?.status !== 0) {
    throw new Error(response?.message?.warning || "Failed to get games");
  }

  return response;
}

export function useAllSlotGames(params: AllSlotGamesParams = {}) {
  return useQuery({
    queryKey: ["slot", "allGames", params],
    queryFn: () => getAllSlotGames(params),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });
}

export type SlotLaunchPayload = {
  code: string;
  vendor?: string;
  provider?: string;
  api?: string;
  id?: string;
  game_id?: string;
  [key: string]: unknown;
};

export async function launchGame(
  lobbyItem: SlotLaunchPayload,
  _loginId: string,
  _token: string
): Promise<SlotGameLaunchResponse> {
  const vendor =
    lobbyItem.vendor || lobbyItem.provider || lobbyItem.api;
  if (!vendor) {
    throw new Error("Missing provider");
  }
  const payload = await apiService.addCommonData({
    game_id: lobbyItem.game_id,
    id: lobbyItem.id,
    code: lobbyItem.code,
    vendor,
    api: vendor,
  });
  const response = await apiService.postRaw<{
    status?: number;
    success?: number;
    result?: { link?: string };
    message?: { warning?: string };
  }>("/api/Slot/gameLaunch", payload);

  const ok = response?.status === 0;
  const link = response?.result?.link ?? "";
  if (!ok || !link) {
    const msg =
      (response as { message?: string })?.message ||
      response?.message?.warning ||
      (typeof (response as { message?: { warning?: string } })?.message === "object"
        ? (response as { message?: { warning?: string } }).message?.warning
        : undefined) ||
      "Failed to launch game";
    throw new Error(typeof msg === "string" ? msg : "Failed to launch game");
  }
  return { result: { link } };
}

export interface SlotCasinoBetHistoryRow {
  id: string;
  gameName: string | null;
  providerName: string | null;
  roundId: string;
  betAmount: number;
  winAmount: number;
  status: number;
  createdAt: string;
}

export interface SlotBettingHistoryResponse {
  status: number;
  histories: SlotCasinoBetHistoryRow[];
  page: number;
  totalPage: number;
  totalCount: number;
}

export async function getSlotBettingHistory(
  params: Record<string, unknown>
): Promise<SlotBettingHistoryResponse> {
  return apiService.postRaw("/api/Slot/getBettingHistory", params);
}

export function useSlotBettingHistory(
  params: Record<string, unknown>,
  enabled: boolean = true
) {
  return useQuery({
    queryKey: ["slot", "slotBettingHistory", params],
    queryFn: () => getSlotBettingHistory(params),
    enabled: apiService.isAuthenticated() && enabled,
    staleTime: 2 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
}
